import _ from 'lodash';

export interface Ancestry {
  itemId: string;
  ancestorId: string;
}

export interface Item {
  id: string;
  name: string;
}

export type HierarchicalItem<T extends Item> = T & {
  ancestralNames: string[];
  children?: string[];
  parentId?: string;
};

export function getAncestralNames(hierarchy: Ancestry[], items: Item[], item: Item): string[] {
  const parent = getParent(hierarchy, items, item);
  if (!parent) {
    return [item.name];
  }

  return getAncestralNames(hierarchy, items, parent).concat(item.name);
}

export function getChildren(hierarchy: Ancestry[], item: Item): Ancestry[] {
  return _.chain(hierarchy).filter({ ancestorId: item.id }).uniq().compact().value();
}

export function getParent<T extends Item>(hierarchy: Ancestry[], items: T[], item: T): T | undefined {
  const ancestor = _.chain(hierarchy).filter({ itemId: item.id }).first().value();

  return ancestor && items.find(item => item.id === ancestor.ancestorId);
}

/**
 * Returns all items that are not itself nor a descendant.
 * Expects all items to already have ancestral name data.
 * @param items all items with ancestral name data.
 * @param child the item to find possible parents for.
 */
export function getPossibleParents<T extends Item>(
  items: HierarchicalItem<T>[],
  child: HierarchicalItem<T>
): HierarchicalItem<T>[] {
  return _.chain(items)
    .cloneDeep()
    .reject(item => {
      // reject if the item is a descendant of the child or the child itself.
      return startsWith(item.ancestralNames, child.ancestralNames);
    })
    .value();
}

export function isHierarchicalItem<T extends Item>(item: T): item is HierarchicalItem<T> {
  return (<HierarchicalItem<T>>item).ancestralNames !== undefined;
}

export function mergeItemsWithHierarchy<T extends Item>(hierarchy: Ancestry[], items: T[]): HierarchicalItem<T>[] {
  return _.map(items, item => {
    const parent = getParent(hierarchy, items, item);
    const children = getChildren(hierarchy, item);

    return {
      ...item,
      ancestralNames: getAncestralNames(hierarchy, items, item),
      parentId: parent?.id,
      children: _(children).isEmpty() ? undefined : _.map(children, 'itemId'),
    };
  });
}

export function sortMergedItems<T extends Item>(hierarchy: Ancestry[], items: T[]): HierarchicalItem<T>[] {
  const hierarchicalItems = mergeItemsWithHierarchy(hierarchy, items);

  const topLevelItems = _.chain(hierarchicalItems)
    .filter(item => !item.parentId)
    .sortBy(['name', 'id'])
    .value();

  const ret: HierarchicalItem<T>[] = [];

  sortSubtrees(topLevelItems);

  function sortSubtrees(items: HierarchicalItem<T>[]): void {
    for (let i = 0; i < items.length; i++) {
      ret.push(items[i]);
      const childItems = _.chain(hierarchicalItems)
        .filter(item => item.parentId === items[i].id)
        .sortBy(['name', 'id'])
        .value();

      if (childItems.length > 0) {
        sortSubtrees(childItems);
      }
    }
  }

  return ret;
}

/**
 * Returns true if the container array contains
 * all elements of the contained array starting from index 0.
 */
function startsWith<T>(container: Array<T>, contained: Array<T>) {
  return _.isEqual(contained, _.take(container, contained.length));
}
